<template>
  <BaseLayout title="Homework List">
    <!-- Main Content (Center) -->
    <div class="container my-4">
      <h1 class="display-4 text-center">List of Homeworks</h1>
      <!-- Loading -->
      <div v-if="loading" class="text-center my-4">
        <div class="spinner-border" role="status">
          <span class="sr-only">&nbsp;&nbsp;Loading...</span>
        </div>
      </div>
      <div v-else>
        <div v-if="homeworks.length === 0" class="alert alert-info text-center">
          No homeworks available.
          <button @click="fetchHomeworks" class="btn btn-primary">
            <i class="bi bi-arrow-clockwise me-2"></i> Refresh
          </button>
        </div>
        <div v-else class="list-group">
          <div v-for="homework in homeworks" :key="homework.id" class="list-group-item d-flex justify-content-between align-items-center">
            <div>
              <h5>{{ homework.title }}</h5>
              <div v-html="renderMarkdown(homework.description)" class="line-clamp"></div>
            </div>
            <div class="d-flex">
              <router-link :to="`/homework/${homework.id}`" class="btn btn-primary btn mx-1 d-flex align-items-center">
                <i class="bi bi-eye me-2"></i>
                <span>View</span>
              </router-link>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Right Sidebar Slot -->
    <template v-slot:right-sidebar>
      <div v-if="isTeacher" class="card p-3 mb-3">
        <h5 class="card-title">Quick Actions:</h5>
        <router-link v-if="isTeacher" to="/homework/create" class="btn btn-primary mb-2">
          <i class="bi bi-plus-circle"></i> Create New Homework
        </router-link>
      </div>
      <div v-if="!isTeacher" class="card p-3 mb-3">
        <h5 class="card-title">Dataset:</h5>
        <button @click="downloadDatasetForStudent" class="btn btn-success" :disabled="isDownloading" v-tooltip="'This action can take a while.'">
          <span v-if="isDownloading" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
          <i class="bi bi-download"></i> Download Dataset
        </button>
      </div>
    </template>

    <StemErrorModal ref="stemErrorModal"/>
  </BaseLayout>
</template>

<script>
import BaseLayout from '@/components/BaseLayout.vue';
import HomeworkController from '@/controllers/HomeworkController';
import StemErrorModal from '@/components/StemErrorModal.vue';
import DatasetController from '@/controllers/DatasetController';
import { marked } from 'marked';
import { mapGetters } from 'vuex';

export default {
  name: 'HomeworkListView',
  components: {
    BaseLayout,
    StemErrorModal
  },
  data() {
    return {
      homeworks: [],
      loading: true,
      isDownloading: false,
    };
  },
  computed: {
    ...mapGetters(['isTeacher', 'userId']),
  },
  async created() {
    try {
      if (this.isTeacher) {
        this.homeworks = await HomeworkController.fetchHomeworks();
      } else {
        this.homeworks = await HomeworkController.fetchHomeworksForStudent();
      }
    } catch (error) {
      this.$refs.stemErrorModal.show('Error', error.message);
    } finally {
      this.loading = false;
    }
  },
  methods: {
    renderMarkdown(text) {
      return marked(text);
    },
    async fetchHomeworks() {
      this.loading = true;
      try {
        if (this.isTeacher) {
          this.homeworks = await HomeworkController.fetchHomeworks();
        } else {
          this.homeworks = await HomeworkController.fetchHomeworksForStudent();
        }
      } catch (error) {
        this.$refs.stemErrorModal.show('Error', error.message);
      } finally {
        this.loading = false;
      }
    },
    async downloadDatasetForStudent() {
      // only for students
      try {
        this.isDownloading = true;
        if (!this.isTeacher) {
          const studentId = this.userId;
          const dataset = await DatasetController.getDatasetForStudent(studentId);
          if (dataset && dataset.file_path) {
            await this.downloadFile(dataset.file_path);
          }
          else {
            this.$store.dispatch('triggerAlert', {
              title: 'Error',
              message: 'No dataset found. Contact teacher, please.',
              type: 'error',
            });
          }
        }
      } catch (error) {
        this.$refs.stemErrorModal.show('Error', error.message);
      } finally {
        this.isDownloading = false;
      }
    },
    async downloadFile(filePath) {
      try {
        const response = await HomeworkController.downloadFile(filePath);
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', this.getFileName(filePath));
        document.body.appendChild(link);
        link.click();
      } catch (error) {
        this.$refs.stemErrorModal.show('Error', error.message);
      }
    },
    getFileName(filePath) {
      return filePath.split('/').pop();
    },
  },
};
</script>

<!-- Max 3 line of description to display -->
<style scoped>
  .line-clamp {
    display: -webkit-box;
    -webkit-line-clamp: 3;
    line-clamp: 3;
    -webkit-box-orient: vertical;  
    overflow: hidden;
  }
</style>